import { reducer as beesReducer } from '@applyboard/redux-bees';
import deepMerge from '../../util/deep_merge';

const fetchRelatedData = (state, payload, parent) => {
  if (!payload || !payload.body || !payload.body.data || !Array.isArray(payload.body.data)) {
    console.warn('invalid payload format:', payload);
    return state;
  }

  const entityList = { ...state.entities[parent.type] };
  let originalParentEntity = null;
  let parentEntity = null;
  try {
    originalParentEntity = entityList[parent.id];
    parentEntity = { ...originalParentEntity };
    if (!originalParentEntity) throw new Error(`cannot find ${parent.type}:${parent.id} in state`);
    entityList[parent.id] = parentEntity;
  } catch (err) {
    console.error('error finding parent entity');
    console.error(err);

    return state;
  }
  parentEntity.relationships = { ...parentEntity.relationships };

  payload.body.data.forEach(entry => {
    if (parentEntity.relationships[entry.type] === originalParentEntity.relationships[entry.type]) {
      parentEntity.relationships[entry.type] = {
        ...parentEntity.relationships[entry.type],
      };
    }

    const currentRelationship = parentEntity.relationships[entry.type];

    if (!currentRelationship.data) {
      currentRelationship.data = [];
    }

    const exists = currentRelationship.data.find(e => e.type === entry.type && e.id === entry.id);

    if (!exists) {
      currentRelationship.data = currentRelationship.data.concat({
        type: entry.type,
        id: entry.id,
      });
    }
  });

  return {
    ...state,
    entities: {
      ...state.entities,
      [parent.type]: entityList,
    },
  };
};

/**
 * Updates the state after deleting some requirement attachments.  The
 * attachment entities themselves need to be removed along with the relationship
 * data structure that associates requirements with requirement attachments
 *
 * It also updates the status of the requirement with the new status, which may
 * have changed due to deleting attachments
 */
const handleDeleteRequirementAttachments = (
  state,
  action: {
    requirementAttachmentIds: Array<string | number>;
    requirementType: 'document' | 'receipt';
    requirementId: string | number;
    requirementStatus: string;
  },
) => {
  let newState = state;

  if (
    !state.entities.requirement_attachments ||
    !action.requirementId ||
    !action.requirementStatus ||
    !action.requirementType
  ) {
    return newState;
  }

  newState = removeRequirementAttachmentEntities(newState, action);

  if (action.requirementType === 'document') {
    newState = updateDocumentRequirementsAfterAttachmentDeletions(newState, action);
  } else {
    newState = updateReceiptRequirementsAfterAttachmentDeletions(newState, action);
  }

  return newState;
};

/**
 * Updates a document requirement with necessary changes after deleting some of
 * its attachments
 */
function updateDocumentRequirementsAfterAttachmentDeletions(
  state,
  action: {
    requirementAttachmentIds: Array<string | number>;
    requirementId: string | number;
    requirementStatus: string;
  },
) {
  const requirementAttachmentIds = action.requirementAttachmentIds.map(id => id.toString());

  const oldDocumentRequirements = state.entities?.application_document_requirements;
  const oldDocumentRequirement = oldDocumentRequirements?.[action.requirementId];
  const oldAttachmentRelationships =
    oldDocumentRequirement?.relationships?.requirement_attachments?.data || [];

  const newDocumentRequirements = oldDocumentRequirement
    ? deepMerge(oldDocumentRequirements, {
        [action.requirementId]: {
          attributes: {
            // There's a new status after deleting attachments.  This can happen,
            // for example, if you delete the last attachment - the requirement
            // changes status to missing
            status: action.requirementStatus,
          },
          relationships: {
            requirement_attachments: {
              // Need to remove any stale relationships to deleted requirement attachments
              data: oldAttachmentRelationships.filter(
                attachment => !requirementAttachmentIds.includes(attachment.id.toString()),
              ),
            },
          },
        },
      })
    : oldDocumentRequirements;

  return {
    ...state,
    entities: {
      ...state.entities,
      application_document_requirements: newDocumentRequirements,
    },
  };
}

/**
 * Updates a receipt requirement with necessary changes after deleting some of
 * its attachments
 */
function updateReceiptRequirementsAfterAttachmentDeletions(
  state,
  action: {
    requirementAttachmentIds: Array<string | number>;
    requirementId: string | number;
    requirementStatus: string;
  },
) {
  const requirementAttachmentIds = action.requirementAttachmentIds.map(id => id.toString());

  const oldReceiptRequirements = state.entities?.application_receipt_requirements;
  const oldReceiptRequirement = oldReceiptRequirements?.[action.requirementId];
  const oldAttachmentRelationships =
    oldReceiptRequirement?.relationships?.requirement_attachments?.data || [];

  const newReceiptRequirements = oldReceiptRequirement
    ? deepMerge(oldReceiptRequirements, {
        [action.requirementId]: {
          attributes: {
            // There's a new status after deleting attachments.  This can happen,
            // for example, if you delete the last attachment - the requirement
            // changes status to missing
            status: action.requirementStatus,
          },
          relationships: {
            requirement_attachments: {
              // Need to remove any stale relationships to deleted requirement attachments
              data: oldAttachmentRelationships.filter(
                attachment => !requirementAttachmentIds.includes(attachment.id.toString()),
              ),
            },
          },
        },
      })
    : oldReceiptRequirements;

  return {
    ...state,
    entities: {
      ...state.entities,
      application_receipt_requirements: newReceiptRequirements,
    },
  };
}

/**
 * Removes some requirement attachments from the entities section
 */
function removeRequirementAttachmentEntities(
  state,
  action: { requirementAttachmentIds: Array<string | number> },
) {
  const newRequirementAttachmentIds = {
    ...state.entities.requirement_attachments,
  };
  for (const requirementAttachmentId of action.requirementAttachmentIds) {
    delete newRequirementAttachmentIds[requirementAttachmentId];
  }

  return {
    ...state,
    entities: {
      ...state.entities,
      requirement_attachments: newRequirementAttachmentIds,
    },
  };
}

const handlePaymentRequirementNewStatus = (
  state,
  action: {
    requirementId: string;
    status: string;
    orderId: string | null | undefined;
  },
) => {
  const previousRequirement = state.entities.application_payment_requirements[action.requirementId];
  if (!previousRequirement) {
    return state;
  }

  const nextRequirement = {
    ...previousRequirement,
    attributes: {
      ...previousRequirement.attributes,
      status: action.status,
      order_id: action.orderId || null,
    },
  };

  return {
    ...state,
    entities: {
      ...state.entities,
      application_payment_requirements: {
        ...state.entities.application_payment_requirements,
        [action.requirementId]: nextRequirement,
      },
    },
  };
};

const addSchoolsToAgent = (state, agency_id, schools) => {
  const previousAgent = state.entities.agencies[agency_id];
  const nextAgent = {
    ...previousAgent,
    relationships: {
      ...previousAgent.relationships,
      schools: {
        data: previousAgent.relationships.schools.data.concat(schools),
      },
    },
  };

  return {
    ...state,
    entities: {
      ...state.entities,
      agencies: {
        ...state.entities.agencies,
        [agency_id]: nextAgent,
      },
    },
  };
};

const removeSchoolsFromAgent = (state, agency_id, schools) => {
  const previousAgent = state.entities.agencies[agency_id];
  const idsToRemove = schools.map(e => parseInt(e.id, 10));
  const nextAgent = {
    ...previousAgent,
    relationships: {
      ...previousAgent.relationships,
      schools: {
        data: previousAgent.relationships.schools.data.filter(
          data => idsToRemove.indexOf(parseInt(data.id, 10)) < 0,
        ),
      },
    },
  };

  return {
    ...state,
    entities: {
      ...state.entities,
      agencies: {
        ...state.entities.agencies,
        [agency_id]: nextAgent,
      },
    },
  };
};

const modifyPutResponse = (state, action, actionName) => {
  const agency_id = action.meta.params[0].id;
  const requestAction = {
    type: `api/${actionName}/request`,
    meta: {
      api: true,
      name: actionName,
      params: [{ id: agency_id }],
      type: 'request',
    },
  };
  const responseAction = {
    type: `api/${actionName}/response`,
    meta: {
      api: true,
      name: actionName,
      params: [{ id: agency_id }],
      type: 'response',
    },
    payload: action.payload,
  };

  let nextState = beesReducer(state, requestAction);
  nextState = beesReducer(state, responseAction);

  return nextState;
};

// TODO: This should have its own file and some auxiliary functions

const updateEntityOnState = (state, entity) => {
  const { type, id } = entity;

  return deepMerge(state, {
    entities: {
      [type]: {
        [id]: entity,
      },
    },
  });
};

const fixRelationships = (prevState, nextState, action) => {
  // TODO: Add support for arrays
  if (!action || !action.payload || !action.payload.body) return nextState;

  const payloadBody = action.payload.body;
  const originalPayloadData = payloadBody.data;
  const previousEntities = prevState.entities;
  const nextEntities = nextState.entities;

  if (
    !action.meta ||
    action.meta.type !== 'response' ||
    typeof originalPayloadData !== 'object' ||
    Array.isArray(originalPayloadData)
  ) {
    return nextState;
  }

  const getNextData = payloadData => {
    const entity_type = payloadData.type;
    const entity_id = payloadData.id;

    const previousDataTypeMap = previousEntities[entity_type];
    if (!previousDataTypeMap) return payloadData;

    const previousData = previousDataTypeMap[entity_id];
    if (!previousData) return payloadData;
    if (!previousData.relationships) return payloadData;
    if (Object.keys(previousData.relationships).length <= 0) return payloadData;

    // Make a copy of the relevant data
    const nextData = {
      ...nextEntities[entity_type][entity_id],
      relationships: nextEntities[entity_type][entity_id].relationships,
    };

    Object.keys(nextData.relationships).forEach(relationshipKey => {
      nextData.relationships[relationshipKey] = {
        ...nextData.relationships[relationshipKey],
      };
    });

    Object.keys(previousData.relationships).forEach(relationshipKey => {
      const relationshipData = nextData.relationships[relationshipKey];
      const previousRelationships = previousData.relationships[relationshipKey];

      if ((relationshipData && relationshipData.data) || !previousRelationships.data) return;

      const newRelationship = {
        ...relationshipData,
        data: previousRelationships.data,
      };

      if (previousRelationships.meta) newRelationship.meta = previousRelationships.meta;

      nextData.relationships[relationshipKey] = newRelationship;
    });

    return nextData;
  };

  const nextData = getNextData(originalPayloadData);

  // Update the state
  let returnState = updateEntityOnState(nextState, nextData);
  if (payloadBody.included && payloadBody.included.length > 0) {
    payloadBody.included.forEach(entity => {
      returnState = updateEntityOnState(returnState, getNextData(entity));
    });
  }

  return returnState;
};

const ignoreActions = ['api/getRequirementCategories/response'];

// List of all entities affected by a given action
const generateUpdateListFromAction = action => {
  const list = [];
  const { data } = action.payload.body;
  if (Array.isArray(data)) {
    data.forEach(entry => {
      list.push(entry);
    });
  } else {
    list.push(data);
  }

  const { included } = action.payload.body;
  if (Array.isArray(included)) {
    included.forEach(entry => {
      list.push(entry);
    });
  }

  return list;
};

// Updates the state with the new entry, respecting existing relationships
// The originalstate parameter is optional and should be used if the relationships
// might possibly be already overwritten by someone (redux-bees).
const updateStateWithEntry = (state, entry, _originalState) => {
  const { id, type } = entry;
  const originalState = _originalState || state;

  const nextState = { ...state };
  nextState.entities = nextState.entities ? { ...nextState.entities } : {};
  const entityTypeCollection = nextState.entities[type] ? { ...nextState.entities[type] } : {};
  nextState.entities[type] = entityTypeCollection;

  let originalRelationships = null;
  try {
    originalRelationships = originalState.entities[type][id].relationships;
  } catch (ex) {
    /* intentionally left blank */
  }

  entityTypeCollection[id] = entry;
  if (!originalRelationships) {
    return nextState;
  }

  entityTypeCollection[id].relationships = {
    ...entityTypeCollection[id].relationships,
  };
  const currentRelationships = entityTypeCollection[id].relationships;
  Object.keys(originalRelationships)
    .filter(key => !!originalRelationships[key].data)
    .forEach(relationshipKey => {
      if (!currentRelationships[relationshipKey] || !currentRelationships[relationshipKey].data) {
        currentRelationships[relationshipKey] = originalRelationships[relationshipKey];
      }
    });

  return nextState;
};

const updateStateWithList = (state, entries, originalState) =>
  entries.reduce((s, e) => updateStateWithEntry(s, e, originalState), state);

const reducer = (state, action) => {
  let nextState = state;

  if (ignoreActions.indexOf(action.type) < 0) {
    nextState = beesReducer(nextState, action);
    try {
      const { type } = action.payload.body.data;
      if (type) {
        if (Array.isArray(nextState.entities[type])) {
          const oldEntities = nextState.entities[type];
          const newEntities = {};
          oldEntities.forEach((e, idx) => {
            if (e) {
              newEntities[idx] = e;
            }
          });

          // this object is already a new one, so, it's ok to assign it like this.
          nextState.entities[type] = newEntities;
        }
      }
    } catch (e) {
      /* intentionally left blank */
    }
  } else {
    // Update only the request information
    const entityName = action.meta.name;
    const requestKey = JSON.stringify(action.meta.params);
    let response = null;
    const payload_data = action.payload.body.data;
    if (Array.isArray(payload_data)) {
      response = payload_data.map(({ id, type }) => ({ id, type }));
    } else if (typeof payload_data === 'object') {
      response = {
        id: payload_data.id,
        type: payload_data.type,
      };
    }
    nextState = deepMerge(nextState, {
      requests: {
        [entityName]: {
          [requestKey]: {
            isLoading: false,
            response,
            meta: action.meta,
            headers: action.payload.headers,
            status: action.payload.status,
          },
        },
      },
    });
  }

  // Handle deletion
  const valid_statuses = [200, 204];
  if (
    action &&
    action.payload &&
    valid_statuses.indexOf(action.payload.status) >= 0 &&
    action.meta &&
    action.meta.params &&
    Array.isArray(action.meta.params)
  ) {
    action.meta.params.forEach(data => {
      if (!data) return;
      if (data.parent && data.parent.id && data.parent.type) {
        nextState = fetchRelatedData(nextState, action.payload, data.parent);
        return;
      }

      if (!data.method) return;
      // @ts-ignore -- typescript is telling method is always a boolean. typescript is wrong.
      if (!data.method === 'destroy') return;

      if (data.type && data.id) {
        // delete an specific id/type pair
        const { type, id } = data;

        // Copies the entity type that we want
        nextState = {
          ...nextState,
          entities: {
            ...nextState.entities,
            [type]: {
              ...nextState.entities[type],
            },
          },
        };

        if (!nextState.entities[type]) {
          console.warn(`not found for deletion: api.entities.${type}`);
        } else if (!nextState.entities[type][id]) {
          console.warn(`not found for deletion: api.entities.${type}.${id}`);
        }

        delete nextState.entities[type][id];
      } else if (data.type && !data.id) {
        console.warn('delete entire type is not yet implemented.');
      } else {
        console.warn("meta says destroy but we're missing the type/id");
      }
    });
  }

  try {
    // Handle makes sure that updates does not disrupt existing fetched data
    nextState = fixRelationships(state, nextState, action);
  } catch (err) {
    console.error('cannot fix relationships:', err);
  }

  // Handle auxiliary calls
  let actionName;

  // eslint-disable-next-line default-case -- we want no action to be taken if type does not match.
  switch (action.type) {
    // I dislike this but I don't see a better way to do this with redux-bees.
    // This file in general violates the single responsibility principle and it
    // mixes different levels of abstraction. If we move requirement attachments to
    // the data folder, this can be removed in favour of setQueryData
    case 'DELETE_REQUIREMENT_ATTACHMENTS':
      nextState = handleDeleteRequirementAttachments(nextState, action);
      break;
    // I dislike this but I don't see a better way to do this with redux-bees.
    // This file in general violates the single responsibility principle and it
    // mixes different levels of abstraction. If we move payment requirements to
    // the data folder, this can be removed in favour of setQueryData
    case 'PAYMENT_REQUIREMENT_NEW_STATUS':
      nextState = handlePaymentRequirementNewStatus(nextState, action);
      break;

    case 'ADD_SCHOOLS_TO_AGENT':
      nextState = addSchoolsToAgent(nextState, action.agency_id, action.value);
      break;

    case 'REMOVE_SCHOOLS_FROM_AGENT':
      nextState = removeSchoolsFromAgent(nextState, action.agency_id, action.value);
      break;

    case 'api/putAgencyManager/response':
      actionName = 'getAgency';
      nextState = deepMerge(modifyPutResponse(nextState, action, actionName), {
        entities: {
          agencies: {
            [action.meta.params[0].id]: {
              relationships: {
                agent_manager: {
                  ...action.meta.params[1],
                },
              },
            },
          },
        },
      });
      break;

    case 'setAgencyUpload':
      nextState = deepMerge(nextState, {
        entities: {
          agencies: {
            [action.agency_id]: {
              relationships: {
                [action.relationship_name]: {
                  data: {
                    type: action.attachment_type,
                    id: action.attachment_id,
                  },
                  meta: null,
                },
              },
            },
          },
          [action.attachment_type]: {
            [action.attachment_id]: action.data,
          },
        },
      });
      break;

    case 'api/getAgency/response':
      actionName = 'getAgency';
      nextState = deepMerge(modifyPutResponse(nextState, action, actionName), {
        entities: {
          agencies: {
            [action.meta.params[0].id]: {
              relationships: {
                agent_manager: {
                  ...action.meta.params[1],
                },
              },
            },
          },
        },
      });
      break;

    case 'api/deleteAgencyManager/response':
      nextState = deepMerge(nextState, {
        entities: {
          agencies: {
            [action.meta.params[0].id]: {
              relationships: {
                agent_manager: {
                  data: null,
                },
              },
            },
          },
        },
      });
      break;

    case 'api/putStudentsAgency/response':
      nextState = deepMerge(nextState, {
        entities: {
          students: {
            [action.meta.params[0].id]: {
              relationships: {
                agency: {
                  data: action.meta.params[1].data,
                },
              },
            },
          },
        },
      });
      break;

    case 'api/destroyStudentsAgency/response':
      nextState = deepMerge(nextState, {
        entities: {
          students: {
            [action.meta.params[0].id]: {
              relationships: {
                agency: {
                  data: null,
                },
              },
            },
          },
        },
      });
      break;

    case 'setUserProfilePicUpload':
      nextState = deepMerge(nextState, {
        entities: {
          users: {
            [action.user_id]: {
              relationships: {
                [action.relationship_name]: {
                  data: {
                    type: action.attachment_type,
                    id: action.attachment_id,
                  },
                  meta: null,
                },
              },
            },
          },
          [action.attachment_type]: {
            [action.attachment_id]: action.data,
          },
        },
      });
      break;

    case 'api/destroyStudentsSalesRep/response':
      nextState = deepMerge(nextState, {
        entities: {
          students: {
            [action.meta.params[0].id]: {
              relationships: {
                sales_representative: {
                  data: null,
                },
              },
            },
          },
        },
      });
      break;

    case 'api/destroyStudentImmigrationConsultant/response':
      nextState = deepMerge(nextState, {
        entities: {
          students: {
            [action.meta.params[0].id]: {
              relationships: {
                immigration_consultant: {
                  data: null,
                },
              },
            },
          },
        },
      });
      break;

    case 'api/getRequirementCategories/response':
      // The relationships from requirement_categories are being overwritten.
      // prevent that here.
      nextState = deepMerge(nextState, {
        entities: {
          requirement_categories: {
            ...action.payload.body.data.reduce(
              // TODO: Remove meta.included if it's false and we already have data.
              (response, data) => ({
                ...response,
                [data.id]: data,
              }),
              {},
            ),
          },
        },
      });
      break;

    // Handle requirement modal json api calls
    case 'api/deleteRequirementAttachment/response':
      nextState = deepMerge(nextState, {
        entities: {
          [action.meta.params[0].type]: {
            [action.meta.params[0].template_id]: {
              relationships: {
                additional_attachments: {
                  data: nextState.entities[action.meta.params[0].type][
                    action.meta.params[0].template_id
                  ].relationships.additional_attachments.data.filter(
                    el => el.id != action.meta.params[0].id,
                  ),
                },
              },
            },
          },
        },
      });
      break;

    case 'api/deleteRequirementExternalLink/response':
      nextState = deepMerge(nextState, {
        entities: {
          [action.meta.params[0].type]: {
            [action.meta.params[0].template_id]: {
              relationships: {
                external_links: {
                  data: nextState.entities[action.meta.params[0].type][
                    action.meta.params[0].template_id
                  ].relationships.external_links.data.filter(
                    el => el.id != action.meta.params[0].id,
                  ),
                },
              },
            },
          },
        },
      });
      break;

    case 'api/putRolePermissions/response':
      nextState = deepMerge(nextState, {
        entities: {
          roles: {
            [action.meta.params[0].id]: {
              relationships: {
                permissions: {
                  data: action.payload.body.data.map(d => ({
                    id: d.id,
                    type: d.type,
                  })),
                },
              },
            },
          },
        },
      });
      break;

    case 'UPDATE_REQUIREMENT_STATUS':
      if (!action.data || !action.data.id || !action.data.attributes) {
        console.warn('invalid action passed to UPDATE_REQUIREMENT_STATUS', action);
        break;
      }
      nextState = deepMerge(nextState, {
        entities: {
          [action.data.type]: {
            [action.data.id]: {
              attributes: {
                status: action.data.attributes.status,
              },
            },
          },
        },
      });
      break;

    case 'ADD_RECEIPT_ATTACHMENT_TO_REQUIREMENT':
      // required stuff:
      // data: the whole attachment data. must be an object that has at least
      // id, type and attributes.
      // requirement_id: the owner of the attachment
      if (!action.data || !action.data.id || !action.data.type || !action.data.attributes) {
        console.warn('invalid action passed to ADD_RECEIPT_ATTACHMENT_TO_REQUIREMENT', action);
        break;
      }
      nextState = deepMerge(nextState, {
        entities: {
          requirement_attachments: {
            [action.data.id]: action.data,
          },
          application_receipt_requirements: {
            [action.requirement_id]: {
              attributes: {
                status: action.data.attributes.requirement_status,
              },
              relationships: {
                requirement_attachments: {
                  data: nextState.entities.application_receipt_requirements[action.requirement_id]
                    .relationships.requirement_attachments.data
                    ? nextState.entities.application_receipt_requirements[
                        action.requirement_id
                      ].relationships.requirement_attachments.data.concat({
                        id: action.data.id,
                        type: action.data.type,
                      })
                    : (nextState.entities.application_receipt_requirements[
                        action.requirement_id
                      ].relationships.requirement_attachments.data = [
                        {
                          id: action.data.id,
                          type: action.data.type,
                        },
                      ]),
                },
              },
            },
          },
        },
      });
      break;

    case 'REQUIREMENT_ATTACHMENT_DELETED':
      if (!action.data || !action.data.id || !action.data.attributes) {
        console.warn('invalid action passed to REQUIREMENT_ATTACHMENT_DELETED', action);
        break;
      }
      nextState = deepMerge(nextState, {
        entities: {
          application_document_requirements: {
            [action.data.id]: {
              attributes: {
                status: action.data.attributes.status,
              },
            },
          },
        },
      });
      break;

    case 'UPDATE_QUESTION_ANSWERED':
      // required stuff:
      // data: requirement id, requirement type and requirement status. must be an object that has at least
      // id, type and status.
      if (!action.data || !action.data.id || !action.data.type || !action.data.attributes) {
        console.warn('invalid action passed to UPDATE_QUESTION_ANSWERED', action);
        break;
      }
      nextState = deepMerge(nextState, {
        entities: {
          application_questions_requirements: {
            [action.requirement_id]: {
              attributes: {
                status: action.data.attributes.requirement_status,
              },
            },
          },
          application_questions: {
            [action.data.id]: {
              attributes: {
                status: action.data.attributes.status,
                status_note: action.data.attributes.status_note,
                answers: action.data.attributes.answers,
              },
            },
          },
        },
      });

      break;

    case 'UPDATE_ANSWER_STATUS':
      if (!action.data || !action.data.id || !action.data.type || !action.data.attributes) {
        console.warn('invalid action passed to UPDATE_ANSWER_STATUS', action);
        break;
      }

      nextState = deepMerge(nextState, {
        entities: {
          application_questions: {
            [action.data.id]: {
              attributes: {
                status: action.data.attributes.status,
                status_note: action.data.attributes.status_note,
                answers: action.data.attributes.answers,
              },
            },
          },
        },
      });

      break;

    case 'UPDATE_ATTACHMENT_STATUS':
      if (!action.data || !action.data.id || !action.data.type || !action.data.attributes) {
        console.warn('invalid action passed to UPDATE_ANSWER_STATUS', action);
        break;
      }
      nextState = deepMerge(nextState, {
        entities: {
          requirement_attachments: {
            [action.data.id]: {
              attributes: {
                status: action.data.attributes.status,
                status_note: action.data.attributes.status_note,
              },
            },
          },
        },
      });

      break;

    case 'REMOVE_MASTER_AGENCY':
      nextState = {
        ...nextState,
        entities: {
          ...nextState.entities,
          agencies: {
            ...nextState.entities.agencies,
            [action.value]: {
              ...nextState.entities.agencies[action.value],
              relationships: {
                ...nextState.entities.agencies[action.value].relationships,
                master_agency: {
                  data: null,
                },
              },
            },
          },
        },
      };
      break;

    // These cases updates the new incoming entities without removing the existing relationships.
    case 'api/getMultipleApplications/response':
      nextState = updateStateWithList(nextState, generateUpdateListFromAction(action), state);
      break;
  }

  return nextState;
};

export default reducer;
